import React from 'react';
import './ServiceStatus.css';

const ServiceStatus = ({ services }) => {
  if (!services) {
    return <div className="loading">Загрузка статуса сервисов...</div>;
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'healthy':
        return 'status-healthy';
      case 'unhealthy':
        return 'status-unhealthy';
      default:
        return 'status-unreachable';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        );
      case 'unhealthy':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        );
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'healthy':
        return 'Online';
      case 'unhealthy':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };

  const getServiceIcon = (name) => {
    switch (name.toLowerCase()) {
      case 'api':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
        );
      case 'data_collector':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <ellipse cx="12" cy="5" rx="9" ry="3"/>
            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
          </svg>
        );
      case 'redis':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <line x1="3" y1="9" x2="21" y2="9"/>
            <line x1="3" y1="15" x2="21" y2="15"/>
            <line x1="9" y1="3" x2="9" y2="21"/>
            <line x1="15" y1="3" x2="15" y2="21"/>
          </svg>
        );
      case 'postgresql':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <ellipse cx="12" cy="5" rx="9" ry="3"/>
            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
            <path d="M3 12v7c0 1.66 4 3 9 3s9-1.34 9-3v-7"/>
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
        );
    }
  };

  const getServiceDisplayName = (name) => {
    const names = {
      'api': 'API Service',
      'data_collector': 'DataCollector',
      'redis': 'Redis',
      'postgresql': 'PostgreSQL'
    };
    return names[name.toLowerCase()] || name;
  };

  const serviceList = Object.entries(services).map(([name, data]) => ({
    name,
    displayName: getServiceDisplayName(name),
    status: data.status,
    info: data.info || null,
    timestamp: data.timestamp || null
  }));

  return (
    <div className="services-grid">
      {serviceList.map((service, index) => (
        <div 
          key={service.name} 
          className={`service-card ${getStatusClass(service.status)}`}
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          <div className="service-header">
            <div className="service-icon">
              {getServiceIcon(service.name)}
            </div>
            <div className="service-status-indicator">
              <span className="status-dot"></span>
            </div>
          </div>
          
          <div className="service-body">
            <h3 className="service-name">{service.displayName}</h3>
            <div className="service-status">
              {getStatusIcon(service.status)}
              <span>{getStatusText(service.status)}</span>
            </div>
          </div>

          {service.info && (
            <div className="service-info">
              {typeof service.info === 'string' ? (
                <p className="info-text">{service.info}</p>
              ) : (
                Object.entries(service.info).map(([key, value]) => (
                  <div key={key} className="info-row">
                    <span className="info-key">{key}:</span>
                    <span className="info-value">{String(value)}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {service.timestamp && (
            <div className="service-timestamp">
              {new Date(service.timestamp).toLocaleTimeString()}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ServiceStatus;
